/**
 * @fileoverview Authentication Context
 * @description Provides authentication state and methods.
 *              The backend only brokers the Ping token exchange (CORS fix).
 *              Tokens are stored in React state (memory) — the backend is
 *              stateless after the one-time code exchange.
 * @module context/AuthContext
 */

import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { exchangeCode, redirectToLogin } from '@/services/api/auth';
import { fetchMyPermissions } from '@/services/api/rbac';
import { apiClient } from '@/services/api/client';
import { appInsights } from '@/telemetry';
import { Base64 } from 'js-base64';

const defaultAuthContext = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  error: null,
  login: () => {},
  logout: () => {},
  getAccessToken: () => null,
  getUserProfile: () => null,
  handleCodeExchange: async () => false,
  userRoles: [],
  userPermissions: {},
  grantedModules: [],
  isAdmin: false,
  rbacLoaded: false,
  hasRole: () => false,
  hasModuleAccess: () => false,
  hasModuleGrant: () => false,
  canRead: () => false,
  canWrite: () => false,
  canDelete: () => false,
  loadPermissions: async () => null,
};

const AuthContext = createContext(defaultAuthContext);

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [error, setError] = useState(null);

  // RBAC state
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState({});
  const [grantedModules, setGrantedModules] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [rbacLoaded, setRbacLoaded] = useState(false);

  /**
   * On mount, check if we have a cached session in sessionStorage.
   * (Survives page refreshes within the same tab, gone when tab closes.)
   */
  useEffect(() => {
    const cached = sessionStorage.getItem('auth_session');
    if (cached) {
      try {
        const data = JSON.parse(cached);
        setIsAuthenticated(true);
        setUser(data.user);
        setAccessToken(data.access_token);
        apiClient.setAccessToken(data.access_token);

        // Restore cached RBAC data temporarily, then refresh from backend
        if (data.rbac) {
          setUserRoles(data.rbac.roles || []);
          setUserPermissions(data.rbac.permissions || {});
          setGrantedModules(data.rbac.granted_modules || []);
          setIsAdmin(data.rbac.is_admin || false);
        }
        // Always re-fetch permissions from backend on page load to stay fresh
        fetchMyPermissions()
          .then((rbac) => {
            if (rbac) {
              setUserRoles(rbac.roles || []);
              setUserPermissions(rbac.permissions || {});
              setGrantedModules(rbac.granted_modules || []);
              setIsAdmin(rbac.is_admin || false);
              const updated = { ...data, rbac };
              sessionStorage.setItem('auth_session', JSON.stringify(updated));
            }
          })
          .catch(() => {})
          .finally(() => setRbacLoaded(true));
      } catch {
        sessionStorage.removeItem('auth_session');
        appInsights.trackException({
          name: 'AuthSessionError',
          message: 'Failed to parse cached auth session',
        });
      }
    }
    setIsLoading(false);
  }, []);

  /**
   * Load the user's RBAC permissions from the backend.
   * Called after login and can be called to refresh permissions.
   */
  const loadPermissions = useCallback(async () => {
    try {
      const rbac = await fetchMyPermissions();
      if (rbac) {
        setUserRoles(rbac.roles || []);
        setUserPermissions(rbac.permissions || {});
        setGrantedModules(rbac.granted_modules || []);
        setIsAdmin(rbac.is_admin || false);
        setRbacLoaded(true);

        // Update session cache with RBAC
        const cached = sessionStorage.getItem('auth_session');
        if (cached) {
          const data = JSON.parse(cached);
          data.rbac = rbac;
          sessionStorage.setItem('auth_session', JSON.stringify(data));
        }
        return rbac;
      }
    } catch (err) {
      appInsights.trackException({
        name: 'LoadPermissionsError',
        message: 'Failed to load permissions',
        error: err,
      });
      console.warn('Failed to load permissions:', err);
    }
    // Even on failure, mark as loaded so the UI doesn't hang
    setRbacLoaded(true);
    return null;
  }, []);

  /**
   * Called from the Callback page after receiving a one-time code
   * from the backend. Exchanges it for actual tokens.
   */
  const handleCodeExchange = useCallback(async (code) => {
    try {
      setIsLoading(true);
      const data = await exchangeCode(code);
      if (data && data.access_token) {
        setIsAuthenticated(true);
        setUser(data.user);
        setAccessToken(data.access_token);
        apiClient.setAccessToken(data.access_token);
        setError(null);
        // Cache in sessionStorage so page refreshes don't lose the session
        sessionStorage.setItem('auth_session', JSON.stringify({
          access_token: data.access_token,
          user: data.user,
        }));

        // Load RBAC permissions after successful auth
        try {
          const rbac = await fetchMyPermissions();
          if (rbac) {
            setUserRoles(rbac.roles || []);
            setUserPermissions(rbac.permissions || {});
            setGrantedModules(rbac.granted_modules || []);
            setIsAdmin(rbac.is_admin || false);
            setRbacLoaded(true);

            // Update session cache with RBAC
            const sessionData = JSON.parse(sessionStorage.getItem('auth_session'));
            sessionData.rbac = rbac;
            sessionStorage.setItem('auth_session', JSON.stringify(sessionData));
          }
        } catch (rbacErr) {
          appInsights.trackException({
            name: 'LoadPermissionsError',
            message: 'Failed to load permissions after login',
            error: rbacErr,
          });
          console.warn('Failed to load permissions after login:', rbacErr);
        } finally {
          // Always mark loaded so the UI never hangs
          setRbacLoaded(true);
        }

        return true;
      } else {
        setError('Token exchange failed');
        return false;
      }
    } catch (err) {
      appInsights.trackException({
        name: 'CodeExchangeError',
        message: 'Failed to exchange code for token',
        error: err,
      });
      console.error('Code exchange failed:', err);
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(() => {
    const returnUrl = sessionStorage.getItem('returnUrl') || '/';
    redirectToLogin(returnUrl);
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken(null);
    setUserRoles([]);
    setUserPermissions({});
    setGrantedModules([]);
    setIsAdmin(false);
    setRbacLoaded(false);
    apiClient.setAccessToken(null);
    sessionStorage.removeItem('auth_session');
    window.location.href = '/login';
  }, []);

  const getAccessToken = useCallback(() => {
    return accessToken;
  }, [accessToken]);

  const getUserProfile = useCallback(() => {
    if (!user) return null;
    
    // Set authenticated user context in Application Insights for better telemetry correlation
    appInsights?.setAuthenticatedUserContext(
      Base64.encode(user.email || user.mail || user.name || 'unknown')
    );
    
    return {
      id: user.sub,
      email: user.email || user.mail,
      name: user.name || user.preferred_username || user.email,
      firstName: user.given_name,
      lastName: user.family_name,
      username: user.preferred_username,
      ...user,
    };
  }, [user]);

  // ──────────────────────────────────────────────
  // RBAC Helper Functions
  // ──────────────────────────────────────────────

  /** Check if the user has a specific role */
  const hasRole = useCallback((role) => {
    if (isAdmin) return true;
    return userRoles.includes(role);
  }, [userRoles, isAdmin]);

  /** Check if the user has access to a module with a specific operation */
  const hasModuleAccess = useCallback((moduleName, operation = 'read') => {
    if (isAdmin) return true;
    const ops = userPermissions[moduleName] || [];
    return ops.includes(operation);
  }, [userPermissions, isAdmin]);

  /** Shortcut: can read a module */
  const canRead = useCallback((moduleName) => hasModuleAccess(moduleName, 'read'), [hasModuleAccess]);

  /** Shortcut: can write to a module */
  const canWrite = useCallback((moduleName) => hasModuleAccess(moduleName, 'write'), [hasModuleAccess]);

  /** Shortcut: can delete in a module */
  const canDelete = useCallback((moduleName) => hasModuleAccess(moduleName, 'delete'), [hasModuleAccess]);

  const hasModuleGrant = useCallback((moduleName) => {
    if (isAdmin) return true;
    if (!moduleName) return true;
    return grantedModules.includes(moduleName);
  }, [grantedModules, isAdmin]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      error,
      login,
      logout,
      getAccessToken,
      getUserProfile,
      handleCodeExchange,
      // RBAC
      userRoles,
      userPermissions,
      grantedModules,
      isAdmin,
      rbacLoaded,
      hasRole,
      hasModuleAccess,
      hasModuleGrant,
      canRead,
      canWrite,
      canDelete,
      loadPermissions,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      error,
      login,
      logout,
      getAccessToken,
      getUserProfile,
      handleCodeExchange,
      userRoles,
      userPermissions,
      grantedModules,
      isAdmin,
      rbacLoaded,
      hasRole,
      hasModuleAccess,
      hasModuleGrant,
      canRead,
      canWrite,
      canDelete,
      loadPermissions,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    appInsights.trackException({
      name: 'AuthContextError',
      message: 'useAuth called outside of AuthProvider',
    });
    return defaultAuthContext;
  }

  return context;
};

export default AuthContext;
